import { Button } from '@chakra-ui/react';
import { useIconData } from 'hooks/useIcon';
import { useAppDispatch } from 'modules/Editor/store';
import { updateWidget } from 'modules/Editor/store/infographSlice';
import { useUser } from 'modules/User';
import { ToolbarDivider } from 'modules/common/components/Toolbar/ToolbarDivider';
import { ReactElement } from 'react';
import { computeSvgViewBox } from 'utils/svg';
import { useEditor, useWidget } from 'widgets/sdk';

import { adjustSVGViewBox } from './IconWidget.helpers';
import { IconWidgetData } from './IconWidget.types';

/**
 * TOOLBAR FOR ADMIN USERS
 *
 * Renders an additional set of ADMIN functionalities for the icon widget toolbar
 *
 * Supports:
 * - Adjust view box for icons8 icons to be tight-fitting
 */
export const AdminIconWidgetToolbar = (): ReactElement | null => {
  const { isAdminUser } = useUser();
  if (!isAdminUser) {
    return null;
  }

  return (
    <>
      <ToolbarDivider />
      <AdjustViewBox />
    </>
  );
};

/**
 * ADJUST VIEW BOX
 *
 * Adjusts the viewbox for icons 8 icons + illustrations to tightly fit the icon
 * May be required if an icon was missing the viewbox attribute from template conversion
 */
const AdjustViewBox = () => {
  const { iconId, iconMetadata, widgetId, widthPx, heightPx, topPx, leftPx } = useWidget<IconWidgetData>();
  const iconData = useIconData({ iconId, iconMetadata });
  const { boundingBox } = useEditor();

  const dispatch = useAppDispatch();

  // Only display for icons that don't have the viewbox calculated already
  if (!iconData || !iconMetadata || iconMetadata.viewBox) {
    return null;
  }

  // Recompute viewbox and adjust width + height accordingly for a tight-fitting BB
  // Will place the icon in the centre of the existing BB
  const handleClick = () => {
    const { svg } = iconData;
    const viewBox = computeSvgViewBox(svg);
    if (!viewBox) {
      return;
    }

    const { width: newWidthPx, height: newHeightPx } = adjustSVGViewBox({
      viewBox,
      maxSize: Math.min(widthPx, heightPx),
    });

    const newTopPx = topPx + (heightPx - newHeightPx) / 2;
    const newLeftPx = leftPx + (widthPx - newWidthPx) / 2;

    dispatch(
      updateWidget({
        widgetId,
        widgetData: {
          widthPx: newWidthPx,
          heightPx: newHeightPx,
          topPx: newTopPx,
          leftPx: newLeftPx,
          iconMetadata: { viewBox },
        },
      }),
    );

    // widget dimensions have changed update the bounding box rect
    setTimeout(() => {
      boundingBox.updateRect();
    }, 0);
  };

  return (
    <Button colorScheme='red' size='sm' onClick={handleClick}>
      Adjust View Box
    </Button>
  );
};
